/**
 * Canonical live-trading hotkey registry (#725).
 *
 * One typed contract shared by the key listener (`Lt2HotkeyListener`), the
 * Settings remap UI, and every surface that TEACHES or PREVIEWS a key — the
 * ⌨ focus chip, the header target readout, the Attack arm tooltip, and the
 * order receipts. Before this file each of those spelled "A/D" and "W/E" out
 * by hand, which is exactly what made the keys unchangeable: a remap would
 * have left six surfaces quietly lying about what the trader's keyboard does.
 *
 * Modelled on `lib/alerts/registry.ts` (#231): every action the hotkey layer
 * exposes has exactly one entry here with a STABLE id, and new optional
 * metadata is ADDED to the entry rather than introduced as a parallel list.
 *
 * ### The id is the action, never the key
 *
 * `LiveHotkeyActionId` names what the action DOES. The key is a mutable
 * property of the action, so remapping changes one field and nothing else —
 * in particular it must never change:
 *
 *   • the `Lt2InputMethod` audit value stamped into the durable `__lt2`
 *     record (`hotkey-a` keeps meaning "the passive top/left action" for
 *     every order ever placed, whatever key fired it);
 *   • the `slot` the fence proves visibility against;
 *   • any grant, ceiling, arming state, or entitlement.
 *
 * A remap is INPUT ROUTING ONLY. There is deliberately no path from this
 * module to a capability: `Lt2HotkeyListener` resolves the action first and
 * then runs every pre-existing gate unchanged.
 *
 * ### Reserved keys are enumerated, not guessed
 *
 * Measured against the codebase, not assumed (#725 sweep). Most of the list is
 * what the app ITSELF binds:
 *
 *   Enter      activation, 17 call sites
 *   Escape     app-wide dismissal (`useDismissibleLayer`) + LT2 focus clear,
 *              fixed by #648's contract and NOT remappable
 *   ArrowUp/Down  EditOrderPriceModal, EditPositionModal,
 *              DecreaseOrderQuantityEditor, GameDetailPanel, OrgCsvExportPanels
 *   Backspace  trader-chip removal in OrgCsvExportPanels
 *
 * Tab is the exception and is listed for a different reason: the app binds it
 * ZERO times: it belongs to the BROWSER's focus navigation, and capturing it
 * would strand anyone who does not use a mouse. Reserved on accessibility
 * grounds, not because something here already listens for it.
 *
 * Anything the trader cannot press alone (a bare modifier) or that the browser
 * owns (function keys) is refused by `keyRefusalReason` rather than listed.
 */
import type { Lt2InputMethod } from './liveIntent';

/** What the action DOES. Stable across any remap; safe to persist. */
export type LiveHotkeyActionId =
  | 'passive-top'
  | 'passive-bottom'
  | 'attack-top'
  | 'attack-bottom'
  | 'pull-all'
  | 'two-sided';

/**
 * The visibility slot the fence proves a displayed price against: 'a' is the
 * top team / left column, 'd' is the bottom team / right column. Attack shares
 * its passive sibling's slot because it fires on the same book and side.
 * `null` for actions that are not a single-sided order (Pull All).
 */
export type LiveHotkeySlot = 'a' | 'd';

export interface LiveHotkeyMeta {
  id: LiveHotkeyActionId;
  /** The factory key. Every existing trader keeps these (#725: "Current
   *  A/D/W/E/Space behavior remains the default for every existing user"). */
  defaultKey: string;
  /** Settings row label — the trader-facing name of the action. */
  label: string;
  /** One line of what the action does, for the Settings hint. */
  description: string;
  /** Which displayed side the fence checks. Null for non-order actions. */
  slot: LiveHotkeySlot | null;
  /** The durable audit stamp. Null for actions that place no order. */
  inputMethod: Lt2InputMethod | null;
  /**
   * False while the action has a reserved id and default key but no wired
   * behaviour. #724 owns `two-sided`; until it lands it is DORMANT.
   */
  implemented: boolean;
  /**
   * The manager grant this action needs before it is usable, or null when it
   * needs none. An action whose grant the trader does not hold is INACTIVE and
   * owns nothing — see `isActionActive`.
   */
  requiresGrant: LiveHotkeyGrantId | null;
}

/** The manager grants that decide whether an action is usable at all. */
export type LiveHotkeyGrantId = 'attack';

/**
 * The trader's live-trading grants, as the registry needs to see them.
 *
 * Deliberately a plain value rather than a store read: the registry is pure so
 * the listener, the Settings panel and the tests all reason about one set of
 * bindings, and a caller cannot accidentally consult a different source.
 */
export interface LiveHotkeyGrants {
  attack: boolean;
}

/** Everything ungranted, for callers with no trader in hand (logged out). */
export const NO_LIVE_HOTKEY_GRANTS: LiveHotkeyGrants = { attack: false };

/**
 * Is this action usable by this trader right now?
 *
 * #725's contract turns on this word. "No two ACTIVE actions may share a key",
 * and "disabled or ungranted actions do not consume their mapped key" — so an
 * action that is dormant (`implemented: false`) or whose grant the trader does
 * not hold neither fires NOR reserves. Both halves matter: reserving without
 * firing is what made a trader without the attack grant unable to reuse W or E
 * for anything, for a capability their account cannot perform.
 */
export function isActionActive(
  id: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): boolean {
  const meta = LIVE_HOTKEY_REGISTRY[id];
  if (!meta.implemented) return false;
  return meta.requiresGrant == null || grants[meta.requiresGrant];
}

export const LIVE_HOTKEY_REGISTRY: Record<LiveHotkeyActionId, LiveHotkeyMeta> = {
  'passive-top': {
    id: 'passive-top',
    defaultKey: 'a',
    label: 'Post top / left side',
    description:
      'Passive post on the focused card — the top team on moneyline, the left column on a two-sided ladder.',
    slot: 'a',
    inputMethod: 'hotkey-a',
    requiresGrant: null,
    implemented: true,
  },
  'passive-bottom': {
    id: 'passive-bottom',
    defaultKey: 'd',
    label: 'Post bottom / right side',
    description:
      'Passive post on the focused card — the bottom team on moneyline, the right column on a two-sided ladder.',
    slot: 'd',
    inputMethod: 'hotkey-d',
    requiresGrant: null,
    implemented: true,
  },
  'attack-top': {
    id: 'attack-top',
    defaultKey: 'w',
    label: 'Attack top / left side',
    description:
      'Bounded aggressive order one level above the displayed bid on the same side as the top / left post. May intentionally take liquidity.',
    slot: 'a',
    inputMethod: 'hotkey-w',
    requiresGrant: 'attack',
    implemented: true,
  },
  'attack-bottom': {
    id: 'attack-bottom',
    defaultKey: 'e',
    label: 'Attack bottom / right side',
    description:
      'Bounded aggressive order one level above the displayed bid on the same side as the bottom / right post. May intentionally take liquidity.',
    slot: 'd',
    inputMethod: 'hotkey-e',
    requiresGrant: 'attack',
    implemented: true,
  },
  'pull-all': {
    id: 'pull-all',
    defaultKey: ' ',
    label: 'Pull All on the focused game',
    description:
      'Cancels every resting Kalshi order on the focused game. Resting orders only; Polymarket and unrelated games are never touched.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: true,
  },
  'two-sided': {
    id: 'two-sided',
    defaultKey: 's',
    label: 'Post both sides',
    description:
      'Posts the top and bottom passive orders together on the focused card. Not yet available — reserved for a later release.',
    slot: null,
    inputMethod: null,
    requiresGrant: null,
    implemented: false,
  },
};

/** Registry order = Settings row order. Stable so the panel does not reshuffle. */
export const LIVE_HOTKEY_ACTION_IDS: readonly LiveHotkeyActionId[] = [
  'passive-top',
  'passive-bottom',
  'attack-top',
  'attack-bottom',
  'pull-all',
  'two-sided',
] as const;

/** A trader's chosen keys, by action. Partial: an absent id means "default". */
export type LiveHotkeyMap = Partial<Record<LiveHotkeyActionId, string>>;

/**
 * Canonical form of a pressed key, so a stored mapping and a live keystroke
 * compare as equals.
 *
 * Letters fold to lowercase — the listener already did this via
 * `e.key.toLowerCase()`, and the fold is what makes a Shift-chord reach the
 * modifier guard as the same key rather than slipping past as 'A' (#609).
 * Both spellings of the space bar collapse to ' ': older engines report
 * 'Spacebar' where current ones report ' '.
 */
export function normalizeHotkey(rawKey: string): string {
  if (rawKey === 'Spacebar') return ' ';
  return rawKey.length === 1 ? rawKey.toLowerCase() : rawKey;
}

/** Keys the app already binds elsewhere. Measured, not assumed — see header. */
export const RESERVED_KEYS: readonly string[] = [
  'Enter',
  'Escape',
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Backspace',
] as const;

/**
 * How a key reads in the UI. Space has no glyph, so it gets a word —
 * "Spacebar", not "Space", because that is what the shipped copy and
 * `docs/live-trading-v3-attack-pull-baseline.md` already call it. Every
 * teaching string now renders through here, so a shorter word would have
 * quietly retitled the shipped Settings row from "Spacebar Pull All" to
 * "Space Pull All" — a copy change nobody asked for.
 */
export function hotkeyDisplayLabel(key: string): string {
  if (key === ' ') return 'Spacebar';
  return key.length === 1 ? key.toUpperCase() : key;
}

/** Bare modifiers, lowercased — `normalizeHotkey` leaves named keys cased. */
const MODIFIER_KEYS = new Set(['shift', 'control', 'alt', 'meta', 'capslock', 'os']);

/**
 * Why this key may not be bound, or null when it is acceptable.
 *
 * Fail-closed and SPECIFIC: the reason doubles as the instruction for picking
 * a workable key, the same discipline the hotkey refusal acks follow.
 * Conflict detection is deliberately NOT here — it needs the whole map, and
 * lives in `conflictingAction` below.
 */
export function keyRefusalReason(rawKey: string): string | null {
  const key = normalizeHotkey(rawKey);
  if (key === '') return 'That key cannot be captured — try another.';
  if (MODIFIER_KEYS.has(key.toLowerCase())) {
    return 'A modifier on its own is not a key — press the key you want to use.';
  }
  if (RESERVED_KEYS.includes(key)) {
    // Accurate for the whole list: Tab is the browser's, the rest are the
    // app's. "The app already uses it" would be false for Tab, and this
    // string is read by a trader.
    return `${hotkeyDisplayLabel(key)} is reserved for moving around and editing orders.`;
  }
  if (/^F\d{1,2}$/.test(key)) {
    return `${key} is a browser key — pick a letter, number, or punctuation key.`;
  }
  // Anything the engine reports as a multi-character name that we have not
  // explicitly allowed is a named/system key (Home, PageDown, Dead, Unidentified,
  // media keys). Refusing the unknown beats binding a key the trader cannot
  // reliably press.
  if (key.length > 1 && key !== ' ') {
    return `${key} is a system key — pick a letter, number, or punctuation key.`;
  }
  return null;
}

/**
 * THE authority on which action owns which key right now.
 *
 * Everything that needs to know "who has W" reads this — the listener, the
 * Settings panel, the conflict check — so there is one answer rather than
 * three that can drift. Inactive actions are absent entirely: they neither
 * fire nor reserve (#725: "disabled or ungranted actions do not consume their
 * mapped key").
 *
 * Two passes, and the order between them IS the safety rule:
 *
 *   1. A key the trader explicitly chose is claimed first.
 *   2. Factory defaults fill in only what is still free.
 *
 * A stored empty string is the explicit UNBOUND marker and never falls through
 * to a default. Nothing writes it today — before this round `sanitizeHotkeyMap`
 * did, to record who lost a collision — but a payload written by an earlier
 * build or edited by hand can still carry one, and honouring it is what stops
 * that action silently reclaiming a key someone else now holds.
 *
 * That is what makes a grant landing safe. Suppose a trader without the attack
 * grant puts Pull All on W — legal, because W belonged to nothing they could
 * use. The day a manager grants Attack, `attack-top` wants its default W back.
 * It does NOT get it: the trader's deliberate choice outranks a factory
 * default, so Attack arrives UNBOUND and its Settings row asks for a key.
 *
 * The alternative — letting the default win — would silently repoint a key the
 * trader had assigned to "cancel my resting orders" at an action that FIRES AN
 * AGGRESSIVE ORDER, with no interaction to mark the change. On a live trading
 * surface those two outcomes are not close, so the tie is broken toward the
 * trader's stated intent every time.
 */
export function activeHotkeyBindings(
  map: LiveHotkeyMap,
  grants: LiveHotkeyGrants,
): ReadonlyMap<LiveHotkeyActionId, string> {
  const bound = new Map<LiveHotkeyActionId, string>();
  const taken = new Set<string>();
  const active = LIVE_HOTKEY_ACTION_IDS.filter((id) => isActionActive(id, grants));

  const claim = (id: LiveHotkeyActionId, key: string): void => {
    if (taken.has(key)) return;
    taken.add(key);
    bound.set(id, key);
  };

  for (const id of active) {
    const chosen = map?.[id];
    // '' is the explicit unbound marker and must not fall through to a default.
    if (chosen == null || chosen === '') continue;
    claim(id, normalizeHotkey(chosen));
  }
  for (const id of active) {
    if (bound.has(id) || map?.[id] === '') continue;
    claim(id, normalizeHotkey(LIVE_HOTKEY_REGISTRY[id].defaultKey));
  }
  return bound;
}

/**
 * Which ACTIVE action already holds `key`, or null when it is free to claim.
 *
 * Grant-aware on purpose. Before #725's second review this walked every action
 * in the registry, so a dormant action (`two-sided` holding S) and an action
 * the trader had no grant for (Attack holding W and E) each blocked a key the
 * trader could never fire. That is the contract mismatch: uniqueness is scoped
 * to ACTIVE actions, and those keys are the trader's to reuse.
 */
export function conflictingAction(
  map: LiveHotkeyMap,
  key: string,
  exceptFor: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): LiveHotkeyActionId | null {
  const wanted = normalizeHotkey(key);
  for (const [id, bound] of activeHotkeyBindings(map, grants)) {
    if (id !== exceptFor && bound === wanted) return id;
  }
  return null;
}

/**
 * Reverse lookup for the listener: which action a keystroke fires, or null.
 *
 * Fail-closed, and now for both reasons: an unmapped key is ordinary typing,
 * exactly as it was before the hotkey layer existed, and an action the trader
 * cannot use is not in the binding table at all — so its default key types
 * normally rather than being swallowed by a capability they lack.
 */
export function actionForKey(
  map: LiveHotkeyMap,
  rawKey: string,
  grants: LiveHotkeyGrants,
): LiveHotkeyActionId | null {
  const key = normalizeHotkey(rawKey);
  for (const [id, bound] of activeHotkeyBindings(map, grants)) {
    if (bound === key) return id;
  }
  return null;
}

/**
 * Drop stored entries that are unusable, so one bad value cannot poison the
 * whole mapping (cf. `savedGames.load`, `displaySettings` field validation).
 *
 * STRUCTURAL validity only. This layer deliberately does NOT decide who wins a
 * collision between a stored key and another action's DEFAULT, because that
 * answer depends on the trader's grants and storage does not know them — it
 * used to, and that is what silently discarded a legal binding: a trader with
 * no attack grant putting Pull All on W had the write dropped on its way to
 * disk while the panel said it had been saved.
 *
 * Effective ownership is resolved once, at read time, by
 * `activeHotkeyBindings`. Keeping intent and resolution apart also means a
 * grant being revoked and restored cannot rewrite what the trader chose.
 */
export function sanitizeHotkeyMap(raw: unknown): LiveHotkeyMap {
  if (!raw || typeof raw !== 'object') return {};
  const source = raw as Record<string, unknown>;
  const out: LiveHotkeyMap = {};
  const takenExplicit = new Set<string>();

  for (const id of LIVE_HOTKEY_ACTION_IDS) {
    const stored = source[id];
    // Absent: no stored intent. Left absent so it resolves to the default at
    // read time, where grants are known.
    if (typeof stored !== 'string') continue;

    const candidate = normalizeHotkey(stored);
    if (candidate === '') {
      out[id] = ''; // deliberately unbound — must not fall through to a default
      continue;
    }
    // Structurally unusable, or a key an earlier action already stored: drop
    // the entry so this action falls back to its default. Registry order
    // decides, so hydration is deterministic.
    if (keyRefusalReason(candidate) != null) continue;
    if (takenExplicit.has(candidate)) continue;

    takenExplicit.add(candidate);
    if (candidate !== LIVE_HOTKEY_REGISTRY[id].defaultKey) out[id] = candidate;
  }
  return out;
}

/**
 * The label a TEACHING surface shows for an action — the trader's own key.
 *
 * Every "press A to…" string in the app resolves through here, so a remap can
 * never leave a hint naming a key that does nothing. An unbound action shows
 * an em dash rather than a key it does not have — and since #725's second
 * review that includes an action which lost its default to a key the trader
 * deliberately assigned elsewhere, or whose grant they do not hold. Naming a
 * key that now fires a DIFFERENT action is the same defect as naming one that
 * fires nothing.
 */
export function hotkeyLabelFor(
  map: LiveHotkeyMap,
  id: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): string {
  const bound = activeHotkeyBindings(map, grants).get(id);
  return bound == null ? '—' : hotkeyDisplayLabel(bound);
}

/**
 * The paired form the A/D and W/E surfaces read as, e.g. "A/D".
 *
 * Kept here rather than built at each call site because the separator is part
 * of the product's vocabulary — the focus chip, the header readout, and the
 * Settings row all say the same thing, and the capture harness matches the
 * chip's text exactly.
 */
export function hotkeyPairLabel(
  map: LiveHotkeyMap,
  first: LiveHotkeyActionId,
  second: LiveHotkeyActionId,
  grants: LiveHotkeyGrants,
): string {
  return `${hotkeyLabelFor(map, first, grants)}/${hotkeyLabelFor(map, second, grants)}`;
}
